<template>
<div class="modal is-large is-active">
    <div class="modal-background"></div>
    <div class="modal-content">
        <div class="custom-modal-box">
            <div class="default-settings-title">
                <span class="account-name">Configure database backup options</span>
                <span class="default-settings-subtitle">Fine tune the backup options of the database.</span>
            </div>

            <div v-if="loadingBackupFlags" class="columns m-top-15">
                <div class="column is-full has-text-centered">
                    <img src="/loader-dark.svg">
                </div>
            </div>

            <div v-if="!loadingBackupFlags" class="backup" style="margin-bottom: 35px;">
                <div class="columns is-multiline">
                    <div class="column is-full" style="padding-bottom: 0;">
                        <span style="font-weight: 700;">Tables included in the backup</span>
                    </div>
                    <div class="column is-full">
                        <div class="field">
                            <input class="is-checkradio is-info" id="all-tables" type="radio" value="all_tables" v-model="targetType">
                            <label for="all-tables">All tables</label>

                            <input class="is-checkradio is-info" id="all-tables-except" type="radio" value="all_tables_except" v-model="targetType">
                            <label for="all-tables-except">All tables except</label>

                            <input class="is-checkradio is-info" id="only-given-tables" type="radio" value="only_given_tables" v-model="targetType">
                            <label for="only-given-tables">Only given tables</label>
                        </div>
                        <div v-if="targetType == 'all_tables_except' || targetType == 'only_given_tables'" class="field">
                            <input type="text" v-model="targetTables" class="input custom-input-size is-medium" placeholder="table1,table2,table3" autocomplete="off"/>
                            <span class="help">{{ tableNamesDescription }}</span>
                        </div>
                    </div>
                    <div v-if="database.type == 'mysql'" class="column is-full" style="padding-bottom: 0;">
                        <span style="font-weight: 700;">mysqldump arguments</span>
                    </div>
                    <div v-if="database.type == 'mysql'" class="column is-4">
                        <div class="field">
                            <input v-model="mysql.singleTransaction" id="single-transaction" type="checkbox" class="switch is-rounded is-info">
                            <label for="single-transaction" style="margin-top:-5px; font-size: 15px;">--single-transaction</label>
                            <span style="display: inline-block; margin-left: 5px;" class="tooltip is-tooltip-top is-tooltip-multiline" data-tooltip="Isolate the dump in a transaction in order to have a consistent output. The resulting backup will have the state of the database at the moment the backup started. This option should be used with the option --skip-lock-tables in order to not lock the tables during the backup process.">
                                <font-awesome-icon :icon="['far', 'question-circle']"></font-awesome-icon>
                            </span>
                        </div>
                    </div>
                    <div v-if="database.type == 'mysql'" class="column is-4">
                        <div class="field">
                            <input v-model="mysql.skipLockTables" id="skip-lock-tables" type="checkbox" class="switch is-rounded is-info">
                            <label for="skip-lock-tables" style="margin-top:-5px; font-size: 15px;">--skip-lock-tables</label>
                            <span style="display: inline-block; margin-left: 5px;" class="tooltip is-tooltip-top is-tooltip-multiline" data-tooltip="By default, mysqldump locks all the tables. Since InnoDB is the default engine and has support for transactions, this option should be enabled in order to avoid tables locking (unless, for some reason, you are using MyISAM).">
                                <font-awesome-icon :icon="['far', 'question-circle']"></font-awesome-icon>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!loadingBackupFlags" class="columns is-multiline" style="margin-top: 10px;">
                <div class="column is-3">
                    <div @click="updateBackupFlags" :disabled="updatingBackupFlags" :class="{ 'is-loading': updatingBackupFlags }" class="button custom-button-size is-fullwidth is-info">Save</div>
                </div>
                <div class="column is-3">
                    <div @click="close" class="close-add-monitor-modal button custom-button-size is-fullwidth is-danger" :disabled="updatingBackupFlags">Cancel</div>
                </div>
            </div>
        </div>
    </div>
    <button @click="close" class="modal-close is-large" aria-label="close"></button>
</div>
</template>

<script>
import Api from '@/api';
import Swal from 'sweetalert2';

export default {
    props: ['database'],

    data() {
        return {
            targetType: '',
            targetTables: '',
            mysql: {
                singleTransaction: false,
                skipLockTables: false,
            },
            loadingBackupFlags: true,
            updatingBackupFlags: false,
        }
    },

    mounted() {
        this.getBackupFlags();
        this.listenEvents();
    },

    methods: {
        close() {
            if (!this.creatingStorage) {
                Event.$emit('configureBackupOptionsModalClosed');
            }
        },

        listenEvents() {
            //
        },

        getBackupFlags() {
            this.loadingBackupFlags = true;

            Api().get('databases/' + this.database.id + '/flags/backup')
                .then((response) => {
                    this.targetType = response.data.target.type;
                    this.targetTables = response.data.target.tables;

                    if (this.database.type == 'mysql') {
                        this.mysql.singleTransaction = this.mysqlOptionIsEnabled('--single-transaction', response.data.options);
                        this.mysql.skipLockTables = this.mysqlOptionIsEnabled('--skip-lock-tables', response.data.options);
                    }
                })
                .finally(() => {
                    this.loadingBackupFlags = false;
                });
        },

        updateBackupFlags() {
            if (this.updatingBackupFlags) {
                return;
            }

            this.updatingBackupFlags = true;

            Api().put('databases/' + this.database.id + '/flags/backup', this.backupFlags)
                .then(() => {
                    Event.$emit('backupFlagsChanged', () => {
                        swal('Success!', 'Backup options changed successfully.', 'success');
                    });
                })
                .catch((error) => {
                     if (error.response.status == 409) {
                        Event.$emit('backupFlagsChanged', () => {
                            Swal.fire({
                                title: 'Oops',
                                text: 'The last backup of this database is still pending or in progress. Wait until it\'s done before changing the backup options.',
                                type: 'error',
                            });
                        });
                    }
                })
                .finally(() => {
                    this.updateBackupFlags = false;
                });
        },

        mysqlOptionIsEnabled(option, enabledOptions) {
            return enabledOptions.indexOf(option) > -1;
        },
    },

    computed: {
        tableNamesDescription() {
            if (this.targetType == 'all_tables_except') {
                return 'Comma separted list with the table names to be excluded. Everything else will be included.';
            }

            if (this.targetType == 'only_given_tables') {
                return 'Comma separted list with the table names to be included. Everything else will be excluded.';
            }
        },

        backupFlags() {
            var options = [];

            if (this.database.type == 'mysql') {
                if (this.mysql.singleTransaction) {
                    options.push('--single-transaction');
                }
                if (this.mysql.skipLockTables) {
                    options.push('--skip-lock-tables');
                }
            }
        
            return {
                target_type: this.targetType,
                target_tables: this.targetTables,
                options: options,
            }
        },
    },
}
</script>
